import React, { useContext } from 'react';
import StateContext from 'common/contexts/state-context';
import TrackingService from 'common/services/Tracking/Tracking';
import LocatorMap from '../../../ui/LocatorMap';
import SalonRow from '../../../ui/SalonRow';
import JsonLd from '../../../ui/JsonLd';
import { getOpeningStatus, timeZoneForAddress } from '../../../services/openingStatus';
import { photoForPlace, LISTING_COPY } from '../../../staticContent';
import { splitSalonName, localpageHref } from '../../../ui/format';

/**
 * `.locator-layout` — carte collante à gauche, liste de salons à droite.
 *
 * ⚠️ `/` sans paramètres de pagination court-circuite côté serveur et renvoie
 * `merchants: []` avec `home_search: true` (§3.3 du plan, vérifié en live).
 * Avant toute recherche ou géolocalisation, la liste est donc naturellement
 * vide : on l'explique plutôt que de laisser un silence.
 */
const LocatorLayout = ({ merchants = [], notFound = false }) => {
  const [state] = useContext(StateContext);
  const origin = state.searchOrigin;

  // Recherche texte sans résultat Geoapify (search.js:62) : sans ce cas
  // distinct, un lieu introuvable retombait sur `emptyBeforeSearch` (première
  // recherche) ou laissait la liste précédente inchangée sans aucun signal —
  // impression que le bouton "Rechercher" ne fait rien.
  if (notFound && !merchants.length) {
    return (
      <div className="locator-layout">
        <LocatorMap title="Carte des salons Tchip Coiffure" />
        <div className="locator-list">
          <div className="locator-empty">
            <p>{LISTING_COPY.searchNotFound}</p>
          </div>
        </div>
      </div>
    );
  }

  if (state.sl && state.sl.home_search && !merchants.length) {
    return (
      <div className="locator-layout">
        <LocatorMap title="Carte des salons Tchip Coiffure" />
        <div className="locator-list">
          <div className="locator-empty">
            <p>{LISTING_COPY.emptyBeforeSearch}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!merchants.length) {
    return (
      <div className="locator-layout">
        <LocatorMap title="Carte des salons Tchip Coiffure" />
        <div className="locator-list">
          <div className="locator-empty">
            <p>{LISTING_COPY.emptyAfterSearch}</p>
          </div>
        </div>
      </div>
    );
  }

  // ItemList JSON-LD (plan §13 Phase 3) — en plus des microdonnées itemProp
  // portées par chaque SalonRow, qui restent la source de vérité visuelle.
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: merchants.map((merchant, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: merchant.name,
      // `merchant.localpageUrl` arrive de l'API en `http://` (cf. `format.js`) ;
      // `localpageHref` sans point de référence applique juste le protocole
      // correct, sans les `fromLat`/`fromLong` inutiles ici (audit SEO §19.3).
      url: localpageHref(merchant.localpageUrl),
    })),
  };

  return (
    <div className="locator-layout">
      <LocatorMap title="Carte des salons Tchip Coiffure à proximité" />
      <div className="locator-list" itemScope itemType="https://schema.org/ItemList">
        <JsonLd data={itemListJsonLd} />
        {merchants.map((merchant, index) => {
          const status = getOpeningStatus(merchant.workingTime, timeZoneForAddress(merchant.address));
          const nameLines = splitSalonName(merchant.name);

          return (
            <SalonRow
              key={merchant.id}
              position={index + 1}
              name={merchant.name}
              nameLines={nameLines}
              photo={photoForPlace(merchant.id)}
              photoAlt={merchant.name}
              // Note Goodays du salon, injectée côté serveur (`api_handler`).
              // Sans stats pour ce salon, `SalonRow` masque déjà le bloc note
              // (`rating > 0`) plutôt que d'afficher un repli.
              rating={merchant.goodaysRating}
              reviewCount={merchant.goodaysReviewCount}
              status={status}
              address={merchant.formattedAddress}
              distanceKm={merchant.distance}
              distanceOrigin={origin}
              phone={merchant.phone}
              // `common/components/StoreCard.js:51` piste l'appel depuis le listing
              // autant que depuis la fiche : les deux points d'appel comptent.
              onPhoneClick={() => TrackingService.trackClickToCall(merchant.id, 'real')}
              localpagePath={merchant.localpageUrl}
            />
          );
        })}
      </div>
    </div>
  );
};

export default LocatorLayout;
